'use client'
export const dynamic = 'force-dynamic'

import { useEffect, useMemo, useState } from 'react'
import Link from 'next/link'
import { CalendarDays, Moon, Sparkles, Star } from 'lucide-react'
import { EmptyState, PageHero } from '../../components/ui/kit'

/* ─────────────────────────────────────────────────────────────
   ISLAMIC CALENDAR (Phase 1 batch 3) — live dates computed from
   fixed Hijri observances via /api/islamic-calendar (Aladhan),
   so the page never goes stale. Old version hardcoded 2025.
───────────────────────────────────────────────────────────── */

interface CalEvent { name: string; desc: string; type: string; date: string; hijri: string }

const FILTERS = ['All', 'Islamic', 'Ramadan', 'Eid']

function fmtDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
}

function daysAway(iso: string) {
  const d = Math.round((new Date(iso).getTime() - Date.now()) / 86_400_000)
  if (d <= 0) return 'Today'
  if (d === 1) return 'Tomorrow'
  return `in ${d} days`
}

export default function IslamicCalendarPage() {
  const [events, setEvents] = useState<CalEvent[] | null>(null)
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState('All')

  useEffect(() => {
    let alive = true
    fetch('/api/islamic-calendar')
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (alive) setEvents(d?.events || null) })
      .catch(() => {})
      .finally(() => { if (alive) setLoading(false) })
    return () => { alive = false }
  }, [])

  const filtered = useMemo(
    () => (events || []).filter((e) => filter === 'All' || e.type === filter),
    [events, filter],
  )

  return (
    <main className="icp">
      <PageHero
        compact
        motif="community"
        eyebrow="Islamic calendar"
        title={<>The year ahead, in both calendars.</>}
        subtitle="Key observances for the next twelve months — computed from their Hijri dates, confirmed by moonsighting."
      />

      <div className="icp-wrap icp-body">
        <div className="icp-filters" role="toolbar" aria-label="Event types">
          {FILTERS.map((f) => (
            <button key={f} type="button" className={filter === f ? 'on' : ''} onClick={() => setFilter(f)}>{f}</button>
          ))}
        </div>

        {loading ? (
          <div className="icp-skeletons">{Array.from({ length: 6 }).map((_, i) => <div key={i} className="icp-skeleton" />)}</div>
        ) : filtered.length ? (
          <div className="icp-list">
            {filtered.map((ev) => (
              <article key={`${ev.name}-${ev.date}`} className={'icp-card' + (ev.type === 'Eid' ? ' gold' : '')}>
                <div className="icp-date">
                  <b>{fmtDate(ev.date)}</b>
                  <span>{daysAway(ev.date)}</span>
                </div>
                <div className="icp-main">
                  <div className="icp-title">
                    {ev.type === 'Eid' ? <Star size={14} strokeWidth={1.5} /> : ev.type === 'Ramadan' ? <Moon size={14} strokeWidth={1.5} /> : <CalendarDays size={14} strokeWidth={1.5} />}
                    <h3>{ev.name}</h3>
                    <span className={'icp-type t-' + ev.type.toLowerCase()}>{ev.type.toUpperCase()}</span>
                  </div>
                  <p>{ev.desc}</p>
                  <span className="icp-hijri">{ev.hijri} AH</span>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <EmptyState
            icon={<CalendarDays size={20} strokeWidth={1.5} />}
            title="Calendar temporarily unavailable"
            body="Dates are computed from the Hijri calendar service. Try again shortly, or check your masjid's announcements."
            cta={{ label: 'Open announcements', href: '/announcements' }}
          />
        )}

        <p className="icp-note"><Sparkles size={12} strokeWidth={1.5} /> Calculated dates — the final observance always follows your local moonsighting announcement. Committees post confirmations in <Link href="/announcements">Announcements &amp; Notices</Link>.</p>
      </div>

      <style jsx>{`
        .icp{background:var(--f-beige);}
        .icp-wrap{max-width:880px;margin:0 auto;padding:0 26px}
        .icp-hero{position:relative;z-index:0;padding:42px 0 34px}
        .icp-hero-bg{position:absolute;left:0;right:0;top:0;bottom:-110px;z-index:-1;pointer-events:none;
          background:url('/hero-art/calendar.svg') right -70px bottom -20px / 520px auto no-repeat,radial-gradient(560px 320px at 18% 30%, rgba(14,124,97,.14), transparent 62%),linear-gradient(170deg,#101A38,#0B132B);
          -webkit-mask-image:linear-gradient(180deg,#000 0%,#000 calc(100% - 150px),transparent 100%);
          mask-image:linear-gradient(180deg,#000 0%,#000 calc(100% - 150px),transparent 100%)}
        .icp-pattern{position:absolute;inset:0;opacity:.04;background-image:radial-gradient(rgba(255,255,255,.85) 1px,transparent 1.5px);background-size:26px 26px}
        .icp-eyebrow{display:inline-flex;align-items:center;gap:8px;font-family:var(--f-mono);font-size:10px;letter-spacing:.18em;color:#14B8A6;border:1px solid rgba(20,184,166,.35);border-radius:99px;padding:4px 12px;background:rgba(20,184,166,.08);margin-bottom:12px}
        .icp-hero h1{font-family:var(--f-display);font-size:clamp(24px,2.8vw,34px);font-weight:700;color:#F6F4EE;letter-spacing:-.01em}
        .icp-hero p{color:rgba(246,244,238,.75);font-size:14px;margin-top:8px;max-width:560px}

        .icp-body{padding-bottom:54px}
        .icp-filters{display:flex;gap:7px;margin:18px 0 16px}
        .icp-filters button{border:1px solid var(--f-line-strong);border-radius:99px;background:#fff;color:var(--f-slate);font-size:12px;font-weight:600;padding:7px 16px;cursor:pointer;font-family:inherit;transition:all .2s}
        .icp-filters button.on{background:var(--f-navy);border-color:var(--f-navy);color:#fff}

        .icp-list{display:grid;gap:10px}
        .icp-card{display:flex;gap:18px;background:#fff;border:1px solid var(--f-line);border-radius:14px;padding:15px 18px;box-shadow:var(--f-shadow)}
        .icp-card.gold{border-color:rgba(212,175,55,.45);background:linear-gradient(150deg,#FDF7E3,#fff)}
        .icp-date{flex:none;width:108px;display:flex;flex-direction:column;gap:2px;padding-top:2px}
        .icp-date b{font-family:var(--f-display);font-size:13.5px;color:var(--f-navy);font-weight:600}
        .icp-date span{font-family:var(--f-mono);font-size:10px;color:var(--f-green);font-weight:600}
        .icp-main{flex:1;min-width:0}
        .icp-title{display:flex;align-items:center;gap:8px;color:var(--f-green)}
        .icp-title h3{font-family:var(--f-display);font-size:14.5px;font-weight:600;color:var(--f-navy)}
        .icp-type{font-family:var(--f-mono);font-size:8.5px;letter-spacing:.1em;border-radius:99px;padding:3px 9px;font-weight:600}
        .t-islamic{color:var(--f-green-deep);background:rgba(14,124,97,.08)}
        .t-ramadan{color:#F6F4EE;background:var(--f-navy)}
        .t-eid{color:var(--f-gold-deep);background:rgba(212,175,55,.14)}
        .icp-main p{font-size:12.5px;color:var(--f-slate);line-height:1.55;margin-top:4px}
        .icp-hijri{display:inline-block;margin-top:6px;font-family:var(--f-mono);font-size:10px;color:var(--f-slate-dim)}

        .icp-skeletons{display:grid;gap:10px}
        .icp-skeleton{height:84px;border-radius:14px;background:linear-gradient(100deg,#EFECE3 30%,#F7F4EC 50%,#EFECE3 70%);background-size:200% 100%;animation:icsk 1.4s linear infinite}
        @keyframes icsk{from{background-position:200% 0}to{background-position:-200% 0}}
        @media(prefers-reduced-motion:reduce){.icp-skeleton{animation:none}}

        .icp-note{display:flex;align-items:flex-start;gap:7px;margin-top:20px;font-size:11.5px;color:var(--f-slate-dim);line-height:1.6}
        .icp-note :global(a){color:var(--f-green);font-weight:600}

        @media(max-width:560px){.icp-card{flex-direction:column;gap:8px}.icp-date{flex-direction:row;gap:10px;width:auto}}
      `}</style>
    </main>
  )
}
